// usePointerLock.ts - Pointer Lock API Management for Spatial OS
// Handles mouse capture for first-person camera controls

import { useState, useCallback, useEffect, useRef } from 'react'

interface PointerLockState {
  isLocked: boolean
  error: string | null
}

interface UsePointerLockOptions {
  onLockChange?: (isLocked: boolean) => void
}

interface UsePointerLockReturn {
  isLocked: boolean
  error: string | null
  requestLock: () => Promise<void>
  exitLock: () => void
  movementX: number
  movementY: number
  consumeMovement: () => { x: number; y: number }
}

export function usePointerLock(options: UsePointerLockOptions = {}): UsePointerLockReturn {
  const { onLockChange } = options
  
  const [state, setState] = useState<PointerLockState>({
    isLocked: false,
    error: null
  })
  
  const movementRef = useRef({ x: 0, y: 0 })
  const [movement, setMovement] = useState({ x: 0, y: 0 })

  // Request pointer lock on document body
  const requestLock = useCallback(async () => {
    try {
      setState(prev => ({ ...prev, error: null }))
      
      if (document.pointerLockElement) {
        // Already locked
        return
      }

      await document.body.requestPointerLock()
    } catch (err) {
      const errorMsg = err instanceof Error ? err.message : 'Pointer lock failed'
      setState(prev => ({ ...prev, error: errorMsg }))
      console.error('[PointerLock] Failed to request lock:', err)
    }
  }, [])

  // Exit pointer lock
  const exitLock = useCallback(() => {
    if (document.pointerLockElement) {
      document.exitPointerLock()
    }
  }, [])

  // Handle pointer lock change events
  useEffect(() => {
    const handleLockChange = () => {
      const isLocked = document.pointerLockElement === document.body
      setState(prev => ({ ...prev, isLocked }))
      
      // Notify callback of lock state change
      onLockChange?.(isLocked)
      
      if (isLocked) {
        console.log('[PointerLock] Mouse captured - Spatial controls active')
      } else {
        console.log('[PointerLock] Mouse released - Spatial controls inactive')
      }
    }

    const handleLockError = (e: Event) => {
      console.error('[PointerLock] Lock error:', e)
      setState(prev => ({ ...prev, error: 'Pointer lock error occurred' }))
    }

    // Handle mouse movement while locked
    const handleMouseMove = (e: MouseEvent) => {
      if (document.pointerLockElement === document.body) {
        movementRef.current.x += e.movementX
        movementRef.current.y += e.movementY
        
        // Update state for React components
        setMovement({ ...movementRef.current })
      }
    }

    document.addEventListener('pointerlockchange', handleLockChange)
    document.addEventListener('pointerlockerror', handleLockError)
    document.addEventListener('mousemove', handleMouseMove)

    return () => {
      document.removeEventListener('pointerlockchange', handleLockChange)
      document.removeEventListener('pointerlockerror', handleLockError)
      document.removeEventListener('mousemove', handleMouseMove)
      
      // Clean up pointer lock on unmount
      if (document.pointerLockElement === document.body) {
        document.exitPointerLock()
      }
    }
  }, [onLockChange])

  // Reset movement deltas after each frame (consumes the movement)
  const consumeMovement = useCallback(() => {
    const delta = { ...movementRef.current }
    movementRef.current = { x: 0, y: 0 }
    setMovement({ x: 0, y: 0 })
    return delta
  }, [])

  return {
    isLocked: state.isLocked,
    error: state.error,
    requestLock,
    exitLock,
    movementX: movement.x,
    movementY: movement.y,
    consumeMovement
  }
}

export default usePointerLock
